import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import { INavigationCard } from '@models/IHeader';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import useIsInIFrame from '@utils/hooks/useIsInIFrame';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

interface Props extends INavigationCard {
  readonly dataTestId?: string;
  readonly hasAnimatedName?: boolean;
  readonly onClick: (params: string) => void;
}

const NavigationCard = ({
  image,
  link,
  hasAnimatedName,
  dataTestId = 'Navigation-Card',
  onClick,
}: Props) => {
  const { name = '', url, target = '_self' } = link;
  const isInIFrame = useIsInIFrame();
  const isMobile = useMediaQuery(1440);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <a
      href={url}
      onClick={() => onClick(name)}
      target={isInIFrame ? '_parent' : target}
      className="body-two relative w-full text-center text-black-100 xl:leading-28"
      data-testid={`${dataTestId}`}
      onMouseEnter={() => {
        if (isMobile) return;
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        if (isMobile) return;
        setIsHovered(false);
      }}
    >
      <div className="mt-8 flex flex-col items-center lg:mt-28 lgm:mr-12">
        <Image
          sizes="300px"
          image={image}
          imageClasses="mb-12 w-full h-40 lg:h-56 object-contain"
          dataTestId={`${dataTestId}-Image`}
        />

        <motion.div className="flex w-full justify-center gap-x-[15px]">
          {/* There is a weird flicker from framer-motion when using the layout prop on the name element,
              noticed when the sub nav tab is open. Most probably it is caused by the fact that the element 
              is placed inside multiple absolute positioned parents, so we only display the animated div
              for the current open tab. */}
          {hasAnimatedName ? (
            <motion.div
              layout
              className={classNames('max-w-[80%]', {
                'font-normal': isHovered,
              })}
            >
              {name}
            </motion.div>
          ) : (
            <div className="max-w-[80%]">{name}</div>
          )}

          <AnimatePresence mode="popLayout">
            {isHovered && (
              <motion.div
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-yellow"
                data-testid={`${dataTestId}-Icon`}
              >
                <Icon name="east" size={IconSizeEnum.sm} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </a>
  );
};

export default NavigationCard;
